import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuickActions = ({ className = "" }) => {
  const navigate = useNavigate();

  const quickActions = [
    {
      id: 'view-logs',
      title: 'View Access Logs',
      description: 'Review recent security events',
      icon: 'FileText',
      color: 'accent',
      path: '/access-logs',
      shortcut: 'Ctrl+L'
    },
    {
      id: 'manage-alerts',
      title: 'Manage Alerts',
      description: 'Configure notification settings',
      icon: 'Bell',
      color: 'warning',
      path: '/alerts-notifications',
      shortcut: 'Ctrl+A'
    },
    {
      id: 'analytics',
      title: 'Security Analytics',
      description: 'View detailed reports',
      icon: 'BarChart3',
      color: 'success',
      path: '/analytics',
      shortcut: 'Ctrl+R'
    },
    {
      id: 'settings',
      title: 'System Settings',
      description: 'Configure access policies',
      icon: 'Settings',
      color: 'secondary',
      path: '/settings',
      shortcut: 'Ctrl+S'
    }
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'accent':
        return 'bg-accent/10 text-accent hover:bg-accent/20';
      case 'warning':
        return 'bg-warning/10 text-warning hover:bg-warning/20';
      case 'success':
        return 'bg-success/10 text-success hover:bg-success/20';
      case 'secondary':
        return 'bg-secondary/10 text-secondary hover:bg-secondary/20';
      default:
        return 'bg-primary/10 text-primary hover:bg-primary/20';
    }
  };

  const handleActionClick = (path) => {
    navigate(path);
  };

  return (
    <div className={`bg-card border border-border rounded-lg p-6 security-shadow-card ${className}`}>
      <div className="flex items-center space-x-3 mb-4">
        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
          <Icon name="Zap" size={18} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Quick Actions</h3>
          <p className="text-sm text-muted-foreground">Frequently used security operations</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {quickActions?.map((action) => (
          <button
            key={action?.id}
            onClick={() => handleActionClick(action?.path)}
            className={`
              ${getColorClasses(action?.color)}
              p-4 rounded-lg text-left security-transition
              hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/20
              group
            `}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Icon 
                  name={action?.icon} 
                  size={20} 
                  className="group-hover:scale-110 security-transition"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-foreground text-sm mb-1">
                  {action?.title}
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  {action?.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs opacity-60 font-mono">
                    {action?.shortcut}
                  </span>
                  <Icon 
                    name="ArrowRight" 
                    size={14} 
                    className="opacity-0 group-hover:opacity-100 security-transition"
                  />
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-border">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Use keyboard shortcuts for faster access
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            iconName="Keyboard"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            View All Shortcuts
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;